import { config } from "config";
import { forgetPooledMaps } from "utils/mapPool.util";

/* Set when a file of Google Maps' own code failed to download (no
   connection). Only then is Maps loaded again (reloadMaps): a file that is
   merely slow to arrive must be left to finish — reloading threw it away
   and started a far longer download over. */
let mapsCodeFailed = false;
window.addEventListener(
  "error",
  (event) => {
    const element = event.target as HTMLScriptElement | null;
    if (
      element?.tagName === "SCRIPT" &&
      /maps\.googleapis\.com\/maps/.test(element.src)
    ) {
      mapsCodeFailed = true;
    }
  },
  true
);

class MapsLoaderService {
  private static susbcribers: any = [];
  static loadingScript: boolean = false;
  static mapsLoaded: boolean = false;
  private static readyPromise: Promise<any> | null = null;

  /* Resolves with `google.maps` once the page's one Maps script is ready.
     Maps must only ever be loaded once: google-map-react, left to itself,
     injected a second copy WITHOUT a key whenever a map opened before
     index.html's script had finished (slow data at start, or the WebView
     reloaded in the background). Two copies on one page broke every map for
     the rest of the session ("This page can't load Google Maps correctly").
     If index.html's script failed outright (no network at start), it is
     loaded again here — once, with the key — and again when the device comes
     back online. Never gives up: a map that turns up late still shows. */
  static whenReady(): Promise<any> {
    const w = window as any;
    if (w.google?.maps?.Map) return Promise.resolve(w.google.maps);
    if (MapsLoaderService.readyPromise) return MapsLoaderService.readyPromise;

    MapsLoaderService.readyPromise = new Promise((resolve) => {
      const check = () => {
        if (!w.google?.maps?.Map) return false;
        window.clearInterval(timer);
        window.removeEventListener("yelo:maps-ready", check);
        window.removeEventListener("online", retry);
        MapsLoaderService.mapsLoaded = true;
        resolve(w.google.maps);
        return true;
      };
      const retry = () => {
        if (!check()) MapsLoaderService.reloadIfFailed();
      };
      const timer = window.setInterval(check, 300);
      window.addEventListener("yelo:maps-ready", check);
      window.addEventListener("online", retry);
      MapsLoaderService.reloadIfFailed();
    });
    return MapsLoaderService.readyPromise;
  }

  /* Loads Google Maps again from scratch. Maps fetches the code that draws
     a map (map.js) only when the first map is made; if that fetch fails —
     the first map opened with no connection — Maps never asks for it again,
     and every map after stays blank until the app restarts. Called only
     after such a failure (mapsCodeFailed). The old copy is
     removed first (script and window.google), so the page never holds two:
     the fresh one starts as on a first load. Maps made by the old copy are
     dropped; whenReady() resolves with the new one. */
  static mapsCodeFailed() {
    return mapsCodeFailed;
  }

  static reloadMaps(): Promise<any> {
    const w = window as any;
    mapsCodeFailed = false;
    document
      .querySelectorAll<HTMLScriptElement>('script[src*="maps.googleapis.com/maps"]')
      .forEach((script) => script.remove());
    delete w.google;
    w.__yeloMapsFailed = false;
    MapsLoaderService.readyPromise = null;
    MapsLoaderService.loadingScript = false;
    MapsLoaderService.mapsLoaded = false;
    forgetPooledMaps();
    return MapsLoaderService.whenReady();
  }

  /* Loads Maps again only when no copy is on its way: index.html's script
     reported an error (window.__yeloMapsFailed, set by its onerror) or none
     is on the page at all. */
  private static reloadIfFailed() {
    const w = window as any;
    if (w.google?.maps?.Map || MapsLoaderService.loadingScript) return;
    const scripts = document.querySelectorAll<HTMLScriptElement>(
      'script[src*="maps.googleapis.com/maps/api/js"]'
    );
    if (scripts.length && !w.__yeloMapsFailed) return;
    scripts.forEach((script) => script.remove());
    w.__yeloMapsFailed = false;
    MapsLoaderService.loadingScript = true;
    MapsLoaderService.LoadScript(
      `https://maps.googleapis.com/maps/api/js?key=${config.GOOGLE_MAPS_API_KEY}&libraries=places`,
      () => (MapsLoaderService.loadingScript = false),
      () => {
        MapsLoaderService.loadingScript = false;
        w.__yeloMapsFailed = true;
      }
    );
  }
  static emitMapsLoadEvent() {
    MapsLoaderService.susbcribers.forEach((f: any) => {
      f();
    });
    MapsLoaderService.susbcribers = [];
    MapsLoaderService.mapsLoaded = true;
  }
  static subscribe(f: Function) {
    MapsLoaderService.susbcribers.push(f);
  }
  static LoadMaps() {
    MapsLoaderService.LoadScript(
      `https://maps.googleapis.com/maps/api/js?key=${config.GOOGLE_MAPS_API_KEY}&libraries=places`,
      MapsLoaderService.emitMapsLoadEvent,
      () => (MapsLoaderService.loadingScript = false)
    );
  }
  static LoadScripts() {
    if (window.google?.maps) {
      MapsLoaderService.mapsLoaded = true;
      MapsLoaderService.emitMapsLoadEvent();
      return;
    }
    const existingScript = document.querySelector<HTMLScriptElement>(
      'script[src*="maps.googleapis.com/maps/api/js"]'
    );
    if (existingScript) {
      MapsLoaderService.loadingScript = true;
      existingScript.addEventListener(
        "load",
        () => {
          if (window.google?.maps) MapsLoaderService.emitMapsLoadEvent();
        },
        { once: true }
      );
      return;
    }
    if (!MapsLoaderService.loadingScript) {
      MapsLoaderService.loadingScript = true;
      MapsLoaderService.LoadMaps();
    }
  }
  private static LoadScript(
    url: string,
    onLoad: any = null,
    onError: any = null
  ) {
    const script = document.createElement("script");
    script.src = url;
    script.async = true;
    script.defer = true;

    if (onLoad) {
      script.onload = onLoad;
    }
    script.onerror = onError;
    document.head.appendChild(script);
  }

  private static LoadInnerScript(url: string, onLoad: any = null) {
    const script = document.createElement("script");
    script.innerHTML = url;
    script.defer = true;
  

    if (onLoad) {
      script.onload = onLoad;
    }
    document.head.appendChild(script);
  }
}

export default MapsLoaderService;
